import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import * as moment from 'moment-timezone';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphButtonModule} from '../../design/oph-button/oph-button.module';
import {OphTimeSelectorComponent} from '../../design/oph-time-selector/oph-time-selector.component';
import {OphXButtonComponent} from '../../design/oph-x-button/oph-x-button.component';
import {PipesModule} from '../../pipes/pipes.module';
interface DialogData {
  timestamp: string;
}

@Component({
  selector: 'oph-timestamp-dialog',
  standalone: true,
  imports: [
    OphXButtonComponent,
    MatDatepickerModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    OphButtonModule,
    OphTimeSelectorComponent,
    PipesModule,
  ],
  templateUrl: './oph-timestamp-dialog.component.html',
  styleUrl: './oph-timestamp-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OphTimestampDialogComponent {
  readonly dialogRef = inject(MatDialogRef<OphTimestampDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  maxDate = moment().tz(this.timezoneService.getTimeZone()).toDate();
  minDate = moment().tz(this.timezoneService.getTimeZone()).subtract(7, 'days').toDate();

  form = new FormGroup({
    date: new FormControl<moment.Moment | null>(null),
    hours: new FormControl<string>('12'),
    minutes: new FormControl<string>('00'),
    ampm: new FormControl<string>('AM'),
  });

  constructor(private timezoneService: TimezoneService) {
    this.setTimestamp();
  }

  setTimestamp() {
    const timestamp = this.data.timestamp;
    const dateMoment = moment(timestamp || undefined).tz(this.timezoneService.getTimeZone());
    this.setDate(dateMoment);
    this.setTime(dateMoment);
  }

  setDate(moment: moment.Moment) {
    this.form.get('date')?.setValue(moment);
  }

  setTime(moment: moment.Moment) {
    const hours = moment.format('h'); // 12-hour format
    const minutes = moment.format('mm');
    const ampm = moment.format('A'); // Returns 'AM' or 'PM'

    this.form.patchValue({
      hours,
      minutes,
      ampm,
    });
  }

  onSave(): void {
    const date = this.form.get('date')?.value;
    const hours = this.form.get('hours')?.value;
    const minutes = this.form.get('minutes')?.value;
    const ampm = this.form.get('ampm')?.value;

    if (!date) return;

    // Create moment in timezone from the moment object
    const timestampMoment = moment(date).tz(this.timezoneService.getTimeZone());
    let hour24 = parseInt(hours || '12');

    // Convert to 24-hour format
    if (ampm === 'AM' && hour24 === 12) {
      hour24 = 0;
    } else if (ampm === 'PM' && hour24 !== 12) {
      hour24 = hour24 + 12;
    }

    timestampMoment.set({
      hour: hour24,
      minute: parseInt(minutes || '0'),
      second: 0,
      millisecond: 0,
    });

    this.dialogRef.close(timestampMoment.toISOString());
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
